// Copyright 2023 Sorama B.V.
import { autoinject, BindingEngine, Disposable } from "aurelia-framework";
import { RecordingGroupContainer } from "datatypes/Containers";
import { GenericMeasurement, MeasurementStorageInfo, RepresentativeDTO, StorageInfo } from "datatypes/SoramaWebApi";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { BusinessUnitRepository } from "repositories/BusinessUnitRepository";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { RepresentativeRepository } from "repositories/RepresentativeRepository";
import { IMeasurementStorageService } from "services/Measurement/IMeasurementStorageService";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import { IStorageService, StorageTypes } from "services/Storage/IStorageService";
import { Measurement, MeasurementFactory } from "./MeasurementTypes";

export type FailedMeasurementInfo = { measurement: Measurement; reason: string };
export type ProgressCallback = (progress: number, failedMeasurements: FailedMeasurementInfo[]) => void;

/**
 * This class is responsible for combining both local and cloud measurements in one collection so that they can be shown and operated on together
 *
 * A recording or measurement can be found either on the cloud, locally, or both
 * Data can be stored locally on the cloud, or both. It can also be on another computer locally.
 */
@autoinject
export class MeasurementCollectionService {
    public measurements: Measurement[] = [];

    public localMeasurementsAvailable = true;

    private subscriptions: Disposable[];
    // Used for caching the username we already retrieved
    private ownerIdNameMap: Map<string, RepresentativeDTO> = new Map();
    public isLoading = false;

    constructor(
        private readonly bindingEngine: BindingEngine,
        private readonly projectManagementService: IProjectManagementService,
        private readonly measurementRepository: MeasurementRepository,
        private readonly storageService: IStorageService,
        private readonly measurementStorageService: IMeasurementStorageService,
        private readonly representativeRepository: RepresentativeRepository,
        private readonly authentication: IAuthentication,
        private readonly measurementFactory: MeasurementFactory,
        private readonly businessUnitRepository: BusinessUnitRepository
    ) {
        this.subscriptions = [
            this.bindingEngine
                .propertyObserver(this.projectManagementService, "_measurements")
                .subscribe(() => this.refreshAllMeasurements()),
            this.bindingEngine
                .propertyObserver(this.measurementStorageService, "isUploading")
                .subscribe(() => this.refreshAllMeasurements()),
        ];
    }

    public dispose() {
        this.subscriptions.forEach((s) => s.dispose());
    }

    //#region Measurement fetching
    public async refreshAllMeasurements(): Promise<void> {
        if (this.projectManagementService.currentProject.status.notAvailable) return;
        if (!this.projectManagementService.currentProject.item) return;
        if (!this.projectManagementService.currentProject.item.id) return;
        if (this.isLoading) return;

        this.isLoading = true;

        let localClientAvailable = true;

        try {
            // Get local and cloud measurements and projectIds in parallel and wait for both to finish
            const [localMeasurements, cloudMeasurements] = await Promise.all([
                this.getLocalMeasurements().catch((e) => {
                    localClientAvailable = false;
                    return [];
                }),
                this.getCloudMeasurements(this.projectManagementService.currentProject.item.id),
            ]);

            // Based on what's registered in this project (info from the cloud) we can determine which measurements we have locally belong to this project
            const [cloudStorageInfos, localAvailableInfos] = await Promise.all([
                this.measurementRepository.getMeasurementStorageInfos(cloudMeasurements.map((m) => m.id)).catch((e) => {
                    console.warn("Failed to get measurement storage infos (likely due to permissions):", e);
                    return [];
                }),
                this.storageService.areMeasurementsAvailable(cloudMeasurements).catch((e) => {
                    localClientAvailable = false;
                    return Object.fromEntries(cloudMeasurements.map((m) => [m.id, false]));
                }),
            ]);

            this.measurements = this.combineMeasurements(
                localMeasurements,
                cloudMeasurements,
                cloudStorageInfos,
                localAvailableInfos
            );

            // These need to make multiple requests and can be done after showing the measurement list
            this.setMeasurementOwners();
            this.setMeasurementFileSizes();
        } catch (e) {
            this.measurements = [];
            this.isLoading = false;
            throw e;
        }

        this.localMeasurementsAvailable = localClientAvailable;
        this.isLoading = false;
    }

    private combineMeasurements(
        localMeasurements: RecordingGroupContainer[],
        cloudMeasurements: GenericMeasurement[],
        cloudStorageInfos: MeasurementStorageInfo[],
        localAvailableInfo: { [key: string]: boolean }
    ): Measurement[] {
        const measurements: Measurement[] = [];

        for (const localMeasurement of localMeasurements) {
            const cloudMeasurementsWithSameId = cloudMeasurements.filter((cloudMeasurement) =>
                localMeasurement.metadata.measurementIds?.some((id) => id === cloudMeasurement.id)
            );
            if (cloudMeasurementsWithSameId.length === 0) {
                measurements.push(this.measurementFactory.createOrphanMeasurement(localMeasurement));
                continue;
            }

            // We can have multiple cloud measurement with the same id as the local measurement
            for (const cloudMeasurement of cloudMeasurementsWithSameId) {
                const dataIsUploaded = cloudStorageInfos?.some(
                    (info) => info.measurementId === cloudMeasurement.id && info.storageInfo === StorageInfo.Available
                );
                if (dataIsUploaded) {
                    measurements.push(
                        this.measurementFactory.createBothMeasurement(localMeasurement, cloudMeasurement)
                    );
                } else {
                    measurements.push(
                        this.measurementFactory.createLocalMeasurement(localMeasurement, cloudMeasurement)
                    );
                }
            }
        }

        for (const cloudMeasurement of cloudMeasurements) {
            // If the cloud measurement is already in the list, skip it (due to 'both' or 'local' measurements in the previous loop)
            if (measurements.some((m) => m.id === cloudMeasurement.id)) continue;

            const localSorAvailable = localAvailableInfo[cloudMeasurement.id];
            const dataIsUploaded = cloudStorageInfos.some(
                (info) => info.measurementId === cloudMeasurement.id && info.storageInfo === StorageInfo.Available
            );
            if (dataIsUploaded) {
                measurements.push(
                    localSorAvailable
                        ? this.measurementFactory.createBothMeasurement(null, cloudMeasurement)
                        : this.measurementFactory.createCloudMeasurement(cloudMeasurement)
                );
            } else {
                measurements.push(
                    localSorAvailable
                        ? this.measurementFactory.createLocalMeasurement(null, cloudMeasurement)
                        : this.measurementFactory.createRemoteMeasurement(cloudMeasurement)
                );
            }
        }

        return measurements;
    }

    private async getLocalMeasurements(): Promise<RecordingGroupContainer[]> {
        const containerSet = await this.storageService.getTypedStorageContainerSet(StorageTypes.recordingGroup);
        return containerSet.containers as RecordingGroupContainer[];
    }

    private async getCloudMeasurements(projectId: string): Promise<GenericMeasurement[]> {
        const cloudMeasurements: GenericMeasurement[] =
            await this.measurementRepository.getMeasurementsForProject(projectId);

        // Remove measurement beamform probes
        return cloudMeasurements.filter(
            (m) =>
                !m.computedMeasurementProperties || m.computedMeasurementProperties?.ProcessorType !== "BeamformProbe"
        );
    }

    private async setMeasurementOwners(): Promise<void> {
        const ownerIds = [...new Set(this.measurements.filter((m) => m.ownerId).map((m) => m.ownerId))];

        try {
            // Get all representatives in parallel and wait for all to finish
            const representatives = await Promise.all(ownerIds.map((ownerId) => this.getOwner(ownerId)));

            // Create a map from id to representative, using index of ownerIds and representatives
            this.ownerIdNameMap = new Map(ownerIds.map((ownerId, index) => [ownerId, representatives[index]]));

            this.measurements.forEach(
                (measurement) => (measurement.owner = this.ownerIdNameMap.get(measurement.ownerId))
            );
        } catch (e) {
            console.warn("Failed to set measurement owners (likely due to permissions):", e);
            // Continue without owner information if there are permissions issues
        }
    }

    private async getOwner(ownerId: string): Promise<RepresentativeDTO | null> {
        let representative: RepresentativeDTO | null = null;

        try {
            representative = await this.representativeRepository.getRepresentative(ownerId);
        } catch {
            // If it's not a representative id, it might be a business unit id, so try to get the name of the first representative of the businessUnit
            try {
                const buRepresentatives = await this.businessUnitRepository
                    .getRepresentativesForBusinessUnit(ownerId)
                    .readToEnd();
                representative = buRepresentatives[0] || null;
            } catch {
                // If both calls fail (likely due to permissions), log and return null
                console.warn(`Failed to get owner info for ID: ${ownerId} (likely due to permissions)`);
                representative = null;
            }
        }
        return representative;
    }

    private async setMeasurementFileSizes(): Promise<void> {
        // Only get file sizes of measurements we have locally and are not uploaded
        const locallyStoredMeasurements = this.measurements.filter(
            (m) => m.dataLocation.has("Local") && !m.dataLocation.has("Cloud")
        );

        const sizes = await this.storageService.getRecordingGroupSizes(
            locallyStoredMeasurements.map((m) => m.containerId)
        );
        locallyStoredMeasurements.forEach((m) => (m.size = sizes.get(m.containerId)));
    }

    //#endregion

    //#region Batch operations
    /**
     * Performs the specified operation on the selected measurements
     * @param measurementIds The ids of the measurements to perform the operation on
     * @param measurementOperation The operation to perform as an async callback function
     * @param progressCallback A callback that is called when the operation is completed for each measurement
     * @returns An array of measurements that failed the operation and the reason why
     */
    public async batchOperation(
        measurementIds: string[],
        measurementOperation: (x: Measurement) => Promise<unknown>,
        progressCallback?: ProgressCallback
    ): Promise<FailedMeasurementInfo[]> {
        const selectedMeasurements = this.measurements.filter((m) => measurementIds.includes(m.id));

        let progress = 0;
        const failedMeasurements: FailedMeasurementInfo[] = [];

        const operations = selectedMeasurements.map((m) => {
            return measurementOperation(m)
                .catch((e) => {
                    failedMeasurements.push({ measurement: m, reason: e.message ?? e });
                })
                .finally(() => {
                    progress += 1 / (selectedMeasurements.length - 1);
                    if (progressCallback) progressCallback(progress, failedMeasurements);
                });
        });

        await Promise.all(operations);
        return failedMeasurements;
    }

    public async batchExport(
        measurementIds: string[],
        progressCallback?: ProgressCallback
    ): Promise<FailedMeasurementInfo[]> {
        // We want to download in parallel
        const downloadPromises = [];

        const selectedMeasurements = this.measurements.filter((m) => measurementIds.includes(m.id));

        if (selectedMeasurements.length === 0) return [];

        // If only one measurement is selected, download it directly
        if (selectedMeasurements.length === 1) {
            try {
                const measurementFile = await selectedMeasurements[0].export();
                FileDownloader.downloadBlobAsFile(measurementFile, measurementFile.type, measurementFile.name);
                return [];
            } catch (e) {
                return [{ measurement: selectedMeasurements[0], reason: e.message ?? e }];
            }
        }

        // If measurements have their data locally download that first
        const localMeasurements = selectedMeasurements.filter((m) => m.dataLocation.has("Local"));

        // Download local sor files
        const localSorMeasurements = localMeasurements.filter((m) => m.sorId);
        if (localSorMeasurements.length > 0) {
            downloadPromises.push(
                this.storageService
                    .getSorZip(localSorMeasurements.map((m) => m.sorId))
                    .then((blob) => FileDownloader.downloadBlobAsFile(blob, blob.type, "localSorMeasurements.zip"))
            );
        }

        // Any other local measurements are downloaded as a container zip
        // Exclude special measurements as they don't have container data
        const localContainerMeasurements = localMeasurements.filter((m) => !m.sorId && !m.isSpecialMeasurement);
        if (localContainerMeasurements.length > 0) {
            // Filter out any measurements without valid container IDs to prevent API errors
            const validContainerIds = localContainerMeasurements
                .map((m) => m.containerId)
                .filter((id) => id && id.trim() !== "");

            if (validContainerIds.length > 0) {
                downloadPromises.push(
                    this.storageService
                        .getDataZip(validContainerIds)
                        .then((blob) => FileDownloader.downloadBlobAsFile(blob, blob.type, "containerMeasurements.zip"))
                );
            }
        }

        // Handle local special measurements individually
        const localSpecialMeasurements = localMeasurements.filter((m) => m.isSpecialMeasurement);
        for (const measurement of localSpecialMeasurements) {
            downloadPromises.push(
                measurement
                    .export()
                    .then((file) => {
                        FileDownloader.downloadBlobAsFile(file, file.type, file.name);
                    })
                    .catch((e) => {
                        console.error(`Failed to export local special measurement ${measurement.name}:`, e);
                        throw e;
                    })
            );
        }

        // Only sor measurements can be downloaded from the cloud
        const selectedMeasurementsNotOnThisPC = selectedMeasurements.filter((m) => !m.dataLocation.has("Local"));

        const sorCloudMeasurements = selectedMeasurementsNotOnThisPC.filter((m) => m.hasSorFileInCloud);
        if (sorCloudMeasurements.length > 0) {
            downloadPromises.push(
                this.measurementRepository
                    .getSorFilesZip(sorCloudMeasurements.map((m) => m.id))
                    .then((blob) => FileDownloader.downloadBlobAsFile(blob, blob.type, "cloudSorMeasurements.zip"))
            );
        }

        // Handle special measurements (JSON) individually
        const specialMeasurements = selectedMeasurementsNotOnThisPC.filter((m) => m.isSpecialMeasurement);
        for (const measurement of specialMeasurements) {
            downloadPromises.push(
                measurement
                    .export()
                    .then((file) => {
                        FileDownloader.downloadBlobAsFile(file, file.type, file.name);
                    })
                    .catch((e) => {
                        console.error(`Failed to export special measurement ${measurement.name}:`, e);
                        throw e;
                    })
            );
        }

        // Remaining measurements that cannot be handled
        const remainingMeasurements = selectedMeasurementsNotOnThisPC.filter(
            (m) => !m.hasSorFileInCloud && !m.isSpecialMeasurement
        );

        try {
            await Promise.all(downloadPromises);
        } catch (e) {
            console.error("Batch download error:", e);
            // If there's an error, we'll still report failed measurements below
        }

        const failedMeasurements: FailedMeasurementInfo[] = remainingMeasurements.map((m) => ({
            measurement: m,
            reason: "Measurement is not stored locally on this computer and is not a supported cloud measurement type",
        }));
        return failedMeasurements;
    }
    //#endregion
}
